<template>
  <avs-card class="rounded-xl overflow-hidden">
    <!-- BODY -->
    <template #content>
      <v-container>
        <v-row>
          <!-- Colonne Utilisateur -->
          <v-col cols="12" md="6">
            <div class="section-title d-flex align-center mb-3">
              <v-icon class="mr-2" color="primary">mdi-account-circle</v-icon>
              <span class="text-subtitle-1 font-weight-medium">{{ $t('Utilisateur') }}</span>
            </div>
            <v-card outlined class="rounded-lg elevation-2">
              <v-list two-line>
                <v-list-item>
                  <v-list-item-avatar size="48" class="elevation-2" color="primary">
                    <span class="white--text text-h6 font-weight-bold">{{ initials }}</span>
                  </v-list-item-avatar>
                  <v-list-item-content>
                    <v-list-item-title class="font-weight-bold capitalize">
                      {{ userFullname }}
                    </v-list-item-title>
                    <v-list-item-subtitle>
                      <v-icon x-small class="mr-1">mdi-phone</v-icon>{{ userPhone }}
                    </v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
                <v-divider class="mx-4 my-1"></v-divider>
                <v-list-item>
                  <v-list-item-icon><v-icon color="grey darken-1">mdi-calendar-account</v-icon></v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title>{{ formatDate(userJoinedAt) }}</v-list-item-title>
                    <v-list-item-subtitle>{{ $t('Date d\'inscription') }}</v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </v-card>
          </v-col>
          
          <!-- Colonne Véhicule -->
          <v-col cols="12" md="6">
            <div class="section-title d-flex align-center mb-3">
              <v-icon class="mr-2" color="primary">mdi-car-info</v-icon>
              <span class="text-subtitle-1 font-weight-medium">{{ $t('Véhicule') }}</span>
            </div>
            <v-card outlined class="rounded-lg elevation-2 pa-4">
              <div class="plate-number text-h5 font-weight-bold mb-3 d-flex align-center">
                <v-icon class="mr-2" color="primary">mdi-card-account-details</v-icon>
                {{ vehicle?.plate_number || '—' }}
              </div>
              
              <div class="mb-3">
                <v-chip 
                  label 
                  small 
                  :color="getVehicleTypeColor(vehicle?.vehicle_type)" 
                  text-color="white"
                  class="mr-2"
                >
                  <v-icon left x-small>{{ getVehicleTypeIcon(vehicle?.vehicle_type) }}</v-icon>
                  {{ vehicleTypeLabel(vehicle?.vehicle_type) }}
                </v-chip>
                
                <v-chip 
                  label 
                  small 
                  :color="statusColor(vehicle?.decision_status)" 
                  text-color="white"
                >
                  <v-icon left x-small>{{ statusIcon(vehicle?.decision_status) }}</v-icon>
                  {{ statusLabel(vehicle?.decision_status) }}
                </v-chip>
              </div>
              
              <v-list dense>
                <v-list-item>
                  <v-list-item-icon><v-icon color="grey darken-1">mdi-calendar</v-icon></v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title>{{ formatDate(vehicle?.created_at) }}</v-list-item-title>
                    <v-list-item-subtitle>{{ $t('Créé le') }}</v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
                
                <v-list-item>
                  <v-list-item-icon>
                    <v-icon :color="vehicle?.is_default ? 'amber darken-2' : 'grey'">
                      {{ vehicle?.is_default ? 'mdi-star' : 'mdi-star-outline' }}
                    </v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title>
                      {{ vehicle?.is_default ? $t('Oui') : $t('Non') }}
                    </v-list-item-title>
                    <v-list-item-subtitle>{{ $t('Véhicule par défaut') }}</v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
                
                <v-list-item>
                  <v-list-item-icon>
                    <v-icon :color="vehicle?.is_active ? 'success' : 'grey'">
                      {{ vehicle?.is_active ? 'mdi-check-circle' : 'mdi-close-circle' }}
                    </v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title>
                      {{ vehicle?.is_active ? $t('Actif') : $t('Inactif') }}
                    </v-list-item-title>
                    <v-list-item-subtitle>{{ $t('Statut d\'activation') }}</v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </v-card>
          </v-col>
        </v-row>
        
        <!-- Pièces jointes -->
        <div class="section-title d-flex align-center mt-6 mb-3">
          <v-icon class="mr-2" color="primary">mdi-file-image</v-icon>
          <span class="text-subtitle-1 font-weight-medium">{{ $t('Pièces jointes') }}</span>
        </div>
        
        <v-row v-if="vehicle?.carte_grise || vehicle?.driving_license">
          <v-col cols="12" md="6" v-if="vehicle?.carte_grise">
            <v-hover v-slot="{ hover }">
              <v-card 
                outlined 
                class="rounded-lg overflow-hidden attachment-card" 
                :elevation="hover ? 8 : 2"
              >
                <v-img 
                  :src="vehicle.carte_grise" 
                  height="200" 
                  class="grey lighten-4" 
                  contain 
                />
                <v-card-actions class="justify-space-between pa-3">
                  <div class="text-subtitle-2 font-weight-medium">
                    <v-icon small class="mr-1">mdi-file-document</v-icon>
                    {{ $t('Carte grise') }}
                  </div>
                  <div>
                    <v-btn small text color="primary" @click="show.carte = true">
                      <v-icon left small>mdi-eye</v-icon>{{ $t('Voir') }}
                    </v-btn>
                    <v-btn 
                      small 
                      text 
                      color="secondary" 
                      :href="vehicle.carte_grise" 
                      target="_blank" 
                      rel="noopener"
                    >
                      <v-icon left small>mdi-download</v-icon>{{ $t('Télécharger') }}
                    </v-btn>
                  </div>
                </v-card-actions>
              </v-card>
            </v-hover>
          </v-col>
          
          <v-col cols="12" md="6" v-if="vehicle?.driving_license">
            <v-hover v-slot="{ hover }">
              <v-card 
                outlined 
                class="rounded-lg overflow-hidden attachment-card" 
                :elevation="hover ? 8 : 2"
              >
                <v-img 
                  :src="vehicle.driving_license" 
                  height="200" 
                  class="grey lighten-4" 
                  contain 
                />
                <v-card-actions class="justify-space-between pa-3">
                  <div class="text-subtitle-2 font-weight-medium">
                    <v-icon small class="mr-1">mdi-card-account-details</v-icon>
                    {{ $t('Permis') }}
                  </div>
                  <div>
                    <v-btn small text color="primary" @click="show.permis = true">
                      <v-icon left small>mdi-eye</v-icon>{{ $t('Voir') }}
                    </v-btn>
                    <v-btn 
                      small 
                      text 
                      color="secondary" 
                      :href="vehicle.driving_license" 
                      target="_blank" 
                      rel="noopener"
                    >
                      <v-icon left small>mdi-download</v-icon>{{ $t('Télécharger') }}
                    </v-btn>
                  </div>
                </v-card-actions>
              </v-card>
            </v-hover>
          </v-col>
        </v-row>
        
        <v-alert v-else type="info" outlined class="mt-3">
          <v-icon left>mdi-information</v-icon>
          {{ $t('Aucune pièce jointe disponible pour ce véhicule') }}
        </v-alert>
        
        <v-row class="mt-4">
          <v-spacer />
          <v-btn outlined color="grey darken-1" @click="$emit('close')">
            <v-icon left>mdi-close</v-icon>{{ $t('commons.close') }}
          </v-btn>
        </v-row>
      </v-container>
    </template>
    
    <!-- VIEWER CARTE GRISE -->
    <v-dialog v-model="show.carte" max-width="1000px">
      <v-card class="rounded-xl">
        <v-toolbar flat color="primary" dark>
          <v-icon class="mr-2">mdi-file-document</v-icon>
          <v-toolbar-title>{{ $t('Carte grise') }}</v-toolbar-title>
          <v-spacer />
          <v-btn icon @click="show.carte=false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>
        <v-img 
          :src="vehicle?.carte_grise" 
          max-height="80vh" 
          contain 
          class="grey lighten-4" 
        />
      </v-card>
    </v-dialog>
    
    <!-- VIEWER PERMIS -->
    <v-dialog v-model="show.permis" max-width="1000px">
      <v-card class="rounded-xl">
        <v-toolbar flat color="primary" dark>
          <v-icon class="mr-2">mdi-card-account-details</v-icon>
          <v-toolbar-title>{{ $t('Permis de conduire') }}</v-toolbar-title>
          <v-spacer />
          <v-btn icon @click="show.permis=false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>
        <v-img 
          :src="vehicle?.driving_license" 
          max-height="80vh" 
          contain 
          class="grey lighten-4" 
        />
      </v-card>
    </v-dialog>
  </avs-card>
</template>

<script>
const VEHICLE_LABELS = {
  VTC: "VTC",
  TAXI_COMPTEUR: "TAXI COMPTEUR",
  TAXI_COMMUNAL: "TAXI COMMUNAL",
  MINI_CAR_URBAIN: "MINI CAR URBAIN",
  MINI_CAR_INTER_URBAIN: "MINI CAR INTER URBAIN",
  AUTO_CAR: "AUTO CAR",
  POIDS_LOURDS: "POIDS LOURDS",
  CAMION: "CAMION",
  CAMIONNETTE: "CAMIONNETTE",
  REVENDEUR: "REVENDEUR",
  APPROVISIONNEMENT: "APPROVISIONNEMENT",
  APPROVISOINNEMENT: "APPROVISIONNEMENT",
  MOTO: "MOTO",
  TRICYCLE: "TRICYCLE",
  PARTICULIER: "PARTICULIER",
  AUTRES: "AUTRES",
  FLOTTE_VTC: "FLOTTE VTC",
};

export default {
  name: "VehicleReviewDialog",
  props: { 
    value: { 
      type: Object, 
      required: true 
    } 
  },
  
  data() {
    return { 
      show: { 
        carte: false, 
        permis: false 
      } 
    };
  },
  
  computed: {
    vehicle() { 
      return this.value || {}; 
    },
    
    isActive() { 
      return !!this.vehicle?.is_active; 
    },
    
    userFullname() {
      const full = (
        this.vehicle?.user_fullname ||
        `${this.vehicle?.user_first_name || ""} ${this.vehicle?.user_last_name || ""}`.trim() ||
        (this.vehicle?.user_username ? `@${this.vehicle.user_username}` : `#${this.vehicle?.user}`)
      );
      return this.capitalize(full);
    },
    
    userPhone() {
      return (
        this.vehicle?.user_phone_number || 
        this.vehicle?.phone_number || 
        this.vehicle?.user_phone || 
        this.$t("Téléphone inconnu")
      );
    },
    
    userJoinedAt() {
      return (
        this.vehicle?.user_date_joined || 
        this.vehicle?.date_joined || 
        this.vehicle?.user_joined_at || 
        null
      );
    },
    
    initials() {
      const s = (this.userFullname || "").trim();
      if (!s) return "U";
      const parts = s.split(/\s+/).slice(0, 2);
      if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
      return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
    },
  },
  
  methods: {
    capitalize(str) {
      if (!str) return '';
      return str
        .toLowerCase()
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
    },
    
    formatDate(datetime) {
      if (!datetime) return '-';
      const d = new Date(datetime);
      return d.toLocaleDateString('fr-FR', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });
    },
    
    vehicleTypeLabel(code) { 
      return VEHICLE_LABELS[code] || code || "-"; 
    },
    
    getVehicleTypeColor(type) {
      const colors = {
        VTC: 'blue',
        TAXI_COMPTEUR: 'orange',
        TAXI_COMMUNAL: 'teal',
        MINI_CAR_URBAIN: 'purple',
        MINI_CAR_INTER_URBAIN: 'deep-purple',
        AUTO_CAR: 'indigo',
        POIDS_LOURDS: 'brown',
        CAMION: 'grey darken-1',
        CAMIONNETTE: 'blue-grey',
        REVENDEUR: 'pink',
        APPROVISIONNEMENT: 'cyan',
        APPROVISOINNEMENT: 'cyan',
        MOTO: 'red',
        TRICYCLE: 'lime darken-2',
        PARTICULIER: 'green',
        AUTRES: 'grey',
        FLOTTE_VTC: 'blue darken-2',
      };
      return colors[type] || 'grey';
    },
    
    getVehicleTypeIcon(type) {
      const icons = {
        VTC: 'mdi-car-side',
        TAXI_COMPTEUR: 'mdi-taxi',
        TAXI_COMMUNAL: 'mdi-taxi',
        MINI_CAR_URBAIN: 'mdi-van-passenger',
        MINI_CAR_INTER_URBAIN: 'mdi-van-utility',
        AUTO_CAR: 'mdi-bus',
        POIDS_LOURDS: 'mdi-truck',
        CAMION: 'mdi-truck-flatbed',
        CAMIONNETTE: 'mdi-truck-delivery',
        REVENDEUR: 'mdi-store',
        APPROVISIONNEMENT: 'mdi-package-variant',
        APPROVISOINNEMENT: 'mdi-package-variant',
        MOTO: 'mdi-motorbike',
        TRICYCLE: 'mdi-bicycle',
        PARTICULIER: 'mdi-car',
        AUTRES: 'mdi-help-circle',
        FLOTTE_VTC: 'mdi-car-multiple',
      };
      return icons[type] || 'mdi-car';
    },
    
    statusColor(st) {
      if (st === true)  return 'success';
      if (st === false) return 'error';
      return 'warning';
    },
    
    statusLabel(st) {
      if (st === true)  return this.$t('Validé');
      if (st === false) return this.$t('Refusé');
      return this.$t('À statuer');
    },
    
    statusIcon(st) {
      if (st === true)  return 'mdi-check-circle';
      if (st === false) return 'mdi-close-circle';
      return 'mdi-clock-alert';
    },
  }
};
</script>

<style scoped>
.opacity-80 { 
  opacity: .8; 
}

.capitalize {
  text-transform: capitalize;
}

.plate-number {
  font-family: 'Courier New', monospace;
  letter-spacing: 1.5px;
}

.attachment-card { 
  transition: transform .2s ease, box-shadow .2s ease; 
}

.attachment-card:hover { 
  transform: translateY(-4px); 
}

.section-title { 
  color: rgba(0,0,0,.7); 
}
</style>